import {trackEvent} from "@/lib/analytics/mixpanel";
import {CloseType} from "@/lib/analytics/close-intent";
import {PopupName} from "@/lib/analytics/dialog-popup-map";
import {PageName} from "@/lib/analytics/page-name";
import {IYupErrorMessage} from "@/lib/types/common/IYupErrorMessage";
import {IBonus} from "@/lib/types/wallet/IBonus";
import {WelcomeBonusStatusesEnum} from "@/lib/enums/WelcomeBonusStatusesEnum";
import {BonusEligibilityEnum} from "@/lib/enums/BonusEligibilityEnum";

/**
 * The Mixpanel event catalogue (AC-711).
 *
 * Naming: `snake_case`, past-tense/verb-object, lowercase enum values. Names are
 * effectively immutable once data is flowing — a rename means a broken funnel
 * and a Lexicon migration — so they are declared here once and only referenced
 * by the typed helpers below.
 *
 * ─── Ownership: FRONTEND vs BACKEND ────────────────────────────────────────
 * The backend is the source of truth for anything financial or authoritative
 * (spec §17–22): `registration_completed`, `login_success`, `deposit_created`,
 * `deposit_success`, `first_deposit_success`, `withdraw_*`, `bonus_*`,
 * `game_session_*`. The frontend MUST NOT emit those — a browser-side duplicate
 * double-counts the funnel and can be fired by a user who never completed the
 * action.
 *
 * The frontend owns *intent* — the funnel steps only the client can see: what
 * the user clicked, opened, and attempted. Hence `registration_submit_clicked`
 * (attempt) rather than `sign_up_completed`, and `deposit_started` (attempt)
 * rather than `deposit_success`. The registration→deposit funnel is assembled
 * in Mixpanel by joining these frontend intent events to the backend's
 * confirmed ones on `user_id`.
 */
export const MixpanelEvents = {
    // ── Base UI (spec §9.1) ──────────────────────────────────────────────────
    /** A route was opened. Fired on pathname change only — see `<MixpanelPageTracker/>`. */
    PAGE_OPENED: "page_opened",
    /** Any orchestrated dialog became visible. Emitted centrally by `<MixpanelDialogBridge/>`. */
    POPUP_OPENED: "popup_opened",
    /** Any orchestrated dialog was removed. Emitted centrally by `<MixpanelDialogBridge/>`. */
    POPUP_CLOSED: "popup_closed",
    /** Generic click. Deliberately bounded by the `ButtonName` union — see below. */
    BUTTON_CLICKED: "button_clicked",
    /** Tab switch inside a component (cashier tabs, profile tabs…). */
    TAB_CLICKED: "tab_clicked",

    // ── Auth (spec §10) — intent only; backend owns the completions ──────────
    /** The registration form was shown. Derived from the AUTH dialog opening on its register tab. */
    REGISTRATION_STARTED: "registration_started",
    /** Registration form submitted. Attempt, not success — backend owns `registration_completed`. */
    REGISTRATION_SUBMIT_CLICKED: "registration_submit_clicked",
    /** The login form was shown. Derived from the AUTH dialog opening on its login tab. */
    LOGIN_STARTED: "login_started",
    /** Login submitted. Attempt, not success — backend owns `login_success`. */
    LOGIN_SUBMIT_CLICKED: "login_submit_clicked",
    /** The password-recovery form was opened from the login form. */
    PASSWORD_RECOVERY_STARTED: "password_recovery_started",
    /** The phone auth flow reached its code step — the SMS is on its way. */
    PHONE_VERIFICATION_STARTED: "phone_verification_started",
    /** An SMS code was submitted in the phone auth flow. Only its length is sent — never the code. */
    PHONE_CODE_ENTERED: "phone_code_entered",

    // ── Cashier (spec §11) ──────────────────────────────────────────────────
    /** The cashier was opened. Derived from the WALLET dialog opening. */
    CASHIER_OPENED: "cashier_opened",
    /** The cashier's deposit tab became the visible one. */
    DEPOSIT_TAB_OPENED: "deposit_tab_opened",
    /** The cashier's withdraw tab became the visible one. */
    WITHDRAW_TAB_OPENED: "withdraw_tab_opened",
    /** A bank payment method or a crypto currency was picked from the cashier grid. */
    PAYMENT_METHOD_SELECTED: "payment_method_selected",
    /** A currency was picked — the fiat payment region, or a crypto currency. */
    CURRENCY_SELECTED: "currency_selected",
    /** A deposit amount was entered. Fired on blur, not per keystroke — see `FiatDepWithdrawFormBuilder`. */
    DEPOSIT_AMOUNT_ENTERED: "deposit_amount_entered",
    /** Deposit request sent to the payment provider. Attempt, not success — backend owns `deposit_success`. */
    DEPOSIT_STARTED: "deposit_started",
    /**
     * A screen with payment instructions became visible after `deposit_started`:
     * the CLABE tile (p2p), the voucher/redirect screen, or the STK-push wait.
     * Closes the AC-815 blind spot — nothing was tracked between `deposit_created`
     * and the provider callback, so a broken instructions step was invisible.
     */
    DEPOSIT_INSTRUCTIONS_SHOWN: "deposit_instructions_shown",
    /** Client-side validation rejected a deposit submit. */
    DEPOSIT_VALIDATION_FAILED: "deposit_validation_failed",
    /** Withdrawal form submitted. Attempt, not success — backend owns `withdraw_success`. */
    WITHDRAW_STARTED: "withdraw_started",
    /** Client-side validation (or a wagering/limit/verification gate) rejected a withdraw submit. */
    WITHDRAW_VALIDATION_FAILED: "withdraw_validation_failed",

    // ── Crypto payments (spec §12) ──────────────────────────────────────────
    /** A network (TRC20 / ERC20 / …) was picked on a crypto deposit or withdrawal. */
    CRYPTO_CHAIN_SELECTED: "crypto_chain_selected",
    /** The crypto deposit address was copied. The address itself is never sent. */
    WALLET_ADDRESS_COPIED: "wallet_address_copied",
    /** The deposit QR code became visible — see `CryptoDepositForm`. */
    QR_CODE_OPENED: "qr_code_opened",
    /** "I paid" on the H2H bank-transfer deposit. Claim, not confirmation — backend owns `deposit_success`. */
    I_PAID_CLICKED: "i_paid_clicked",

    // ── Bonus (spec §13) ────────────────────────────────────────────────────
    /** The bonuses page was opened. Derived from `page_opened` on the bonuses route. */
    BONUS_PAGE_OPENED: "bonus_page_opened",
    /** A welcome-bonus banner inside the WELCOME_BONUS dialog was acted on. */
    WELCOME_BONUS_CARD_CLICKED: "welcome_bonus_card_clicked",
    /** "Use" on a bonus card — the deposit the bonus needs. */
    BONUS_USE_CLICKED: "bonus_use_clicked",
    /** "Activate" / "Resume" with no other bonus wagering. Intent — backend owns `bonus_active`. */
    BONUS_TURN_ON_CLICKED: "bonus_turn_on_clicked",
    /** "Activate" / "Resume" while another bonus is ACTIVE — the app pauses that one. */
    BONUS_SWITCH_CLICKED: "bonus_switch_clicked",
    /** The allowed-games list on a bonus's conditions was opened. */
    BONUS_ALLOWED_GAMES_OPENED: "bonus_allowed_games_opened",

    // ── Game (spec §14) ─────────────────────────────────────────────────────
    /** A game launched. Emitted by the game page, so a deep link counts too. */
    GAME_OPENED: "game_opened",
    /** "Play" on a free-spins bonus — the click that routes into the game. */
    PLAY_CLICKED: "play_clicked",
    /** The game page was left. Carries `duration_sec`. */
    GAME_CLOSED: "game_closed",

    // ── Profile / verification (spec §15) ───────────────────────────────────
    /** The account (avatar) popover was opened — the app's profile surface. */
    PROFILE_OPENED: "profile_opened",
    /** A phone number was submitted for verification. The number is never sent. */
    ADD_PHONE_CLICKED: "add_phone_clicked",
    /** The SMS code was submitted. Attempt — the backend owns `phone_verification_success`. */
    CONFIRM_PHONE_CLICKED: "confirm_phone_clicked",
    /** An email was submitted for verification. The address is never sent. */
    ADD_EMAIL_CLICKED: "add_email_clicked",
    /** The email code was submitted. Attempt — the backend owns `email_verification_success`. */
    CONFIRM_EMAIL_CLICKED: "confirm_email_clicked",

    // ── PWA / push (spec §16) ───────────────────────────────────────────────
    /** The install toast became visible. */
    PWA_INSTALL_PROMPT_SHOWN: "pwa_install_prompt_shown",
    /** "Install" on the toast — the browser's own prompt opens next. */
    PWA_INSTALL_CLICKED: "pwa_install_clicked",
    /** The browser reported the app was installed. */
    PWA_INSTALLED: "pwa_installed",
    /** The app was launched from the installed icon, not a browser tab. */
    PWA_OPENED: "pwa_opened",
    /** The browser's notification permission prompt was raised. */
    PUSH_PERMISSION_REQUESTED: "push_permission_requested",
    PUSH_PERMISSION_GRANTED: "push_permission_granted",
    PUSH_PERMISSION_DENIED: "push_permission_denied",
} as const;

/**
 * The surface a click came from. Closed union — `component` is a breakdown
 * dimension, and a free-form string would make it unusable.
 */
export type ComponentName =
    | "header"
    | "mobile_menu"
    | "popup"
    | "cashier"
    | "bonus_card"
    | "game"
    | "profile"
    | "search";

/**
 * Every click we are willing to track. `button_clicked` is how an analytics
 * project gets expensive (Mixpanel bills per event) and how a Lexicon fills with
 * junk — so the names are enumerated here and nowhere else. Adding a click means
 * adding a member; there is no free-form escape hatch on purpose.
 */
export type ButtonName =
    | "close_popup"
    | "switch_to_register"
    | "switch_to_login"
    | "registration_submit"
    | "login_submit"
    | "google_auth_click"
    | "password_recovery_click"
    | "open_cashier"
    | "open_bonuses"
    | "play_click"
    | "copy_user_id";

/**
 * Tabs the app exposes (spec §9.1).
 *
 * `phone` / `email` are the auth dialog's provider tabs — the choice of how to
 * sign in, taken before the registration funnel's first submit. They extend the
 * spec's list because the spec enumerates the cashier's tabs only; collapsing
 * them into one another (or leaving them untracked) would hide which provider
 * the country default actually steers people onto — see `AuthResolver`.
 */
export type TabName =
    | "deposit"
    | "withdraw"
    | "history"
    | "profile"
    | "security"
    | "casino"
    | "menu"
    | "search"
    | "phone"
    | "email";

interface PageOpenedProps {
    page: PageName;
    /** The page navigated away from; `null` on the first page of the visit. */
    previousPage: PageName | null;
}

export const trackPageOpened = (props: PageOpenedProps): void => {
    trackEvent(MixpanelEvents.PAGE_OPENED, {
        page: props.page,
        previous_page: props.previousPage,
    });
};

interface PopupOpenedProps {
    popupName: PopupName;
    /** The page the popup was opened from. */
    page: PageName;
}

export const trackPopupOpened = (props: PopupOpenedProps): void => {
    trackEvent(MixpanelEvents.POPUP_OPENED, {
        popup_name: props.popupName,
        page: props.page,
        component: "popup",
    });
};

interface PopupClosedProps {
    popupName: PopupName;
    closeType: CloseType;
}

export const trackPopupClosed = (props: PopupClosedProps): void => {
    trackEvent(MixpanelEvents.POPUP_CLOSED, {
        popup_name: props.popupName,
        close_type: props.closeType,
    });
};

interface ButtonClickedProps {
    buttonName: ButtonName;
    component: ComponentName;
    page: PageName;
}

export const trackButtonClicked = (props: ButtonClickedProps): void => {
    trackEvent(MixpanelEvents.BUTTON_CLICKED, {
        button_name: props.buttonName,
        component: props.component,
        page: props.page,
    });
};

interface TabClickedProps {
    tabName: TabName;
    component: ComponentName;
}

export const trackTabClicked = (props: TabClickedProps): void => {
    trackEvent(MixpanelEvents.TAB_CLICKED, {
        tab_name: props.tabName,
        component: props.component,
    });
};

/**
 * How the player is registering. Mirrors `LoginMethod`, and omits `google` for the
 * same reason: the Google button is an OAuth redirect that cannot be told apart
 * from a Google *login* at the click.
 */
export type RegistrationMethod = "email" | "phone";

export const trackRegistrationStarted = (method: RegistrationMethod): void => {
    trackEvent(MixpanelEvents.REGISTRATION_STARTED, {registration_method: method});
};

export const trackLoginStarted = (): void => {
    trackEvent(MixpanelEvents.LOGIN_STARTED);
};

/**
 * How the player is signing in. The spec's third value, `google`, is deliberately
 * absent: the Google button is an OAuth redirect, not a login submit — it cannot
 * even be told apart from a Google *registration* at the click, and what comes
 * back is the backend's `login_success` to report. `button_clicked`
 * (`google_auth_click`) is what covers that click.
 */
export type LoginMethod = "email" | "phone";

export const trackLoginSubmitClicked = (method: LoginMethod): void => {
    trackEvent(MixpanelEvents.LOGIN_SUBMIT_CLICKED, {login_method: method});
};

export const trackPasswordRecoveryStarted = (): void => {
    trackEvent(MixpanelEvents.PASSWORD_RECOVERY_STARTED);
};

export const trackPhoneVerificationStarted = (): void => {
    trackEvent(MixpanelEvents.PHONE_VERIFICATION_STARTED);
};

/**
 * The SMS code was submitted. `code_length` is the spec's privacy-preserving
 * stand-in for the code — the code itself must never reach Mixpanel (spec §8),
 * and `sanitize.ts` only backstops that.
 */
export const trackPhoneCodeEntered = (codeLength: number): void => {
    trackEvent(MixpanelEvents.PHONE_CODE_ENTERED, {code_length: codeLength});
};

/**
 * Where the cashier was opened from (spec §11). Not knowable from the dialog
 * lifecycle alone, so every call site that opens the WALLET dialog passes it in
 * the request's props and `<MixpanelDialogBridge/>` reads it back.
 *
 * `mobile_menu` extends the spec's list: the mobile bottom menu is a distinct
 * surface from the desktop header and collapsing the two would hide which one
 * actually drives deposits.
 *
 * `insufficient_funds` is the one source that is not a click: the game page
 * opens the cashier by itself when the wallet rejects a bet (AC-835). It is
 * kept apart from `game` — the provider's own Deposit button — so the
 * auto-open funnel can be measured, and it is the only source that must NOT
 * be derived into `button_clicked` (see `MixpanelDialogBridge`).
 */
export type CashierOpenSource =
    | "header"
    | "mobile_menu"
    | "bonus_card"
    | "profile"
    | "game"
    | "popup"
    | "insufficient_funds";

interface CashierOpenedProps {
    /** The tab the cashier opened on. */
    defaultTab: TabName;
    /** `null` only if a call site opened WALLET without declaring its source. */
    openSource: CashierOpenSource | null;
}

export const trackCashierOpened = (props: CashierOpenedProps): void => {
    trackEvent(MixpanelEvents.CASHIER_OPENED, {
        default_tab: props.defaultTab,
        open_source: props.openSource,
    });
};

export const trackBonusPageOpened = (): void => {
    trackEvent(MixpanelEvents.BONUS_PAGE_OPENED);
};

interface RegistrationSubmitClickedProps {
    method: RegistrationMethod;
    /**
     * Whether a promo code was supplied (AC-577 replaced the dormant referral
     * code field with it). The code itself is never sent. Always false for
     * phone: that method's promo input lives on the later verify step, after
     * this event has already fired.
     */
    hasPromoCode: boolean;
}

export const trackRegistrationSubmitClicked = (
    props: RegistrationSubmitClickedProps,
): void => {
    trackEvent(MixpanelEvents.REGISTRATION_SUBMIT_CLICKED, {
        method: props.method,
        has_promo_code: props.hasPromoCode,
    });
};

interface DepositStartedProps {
    /**
     * Deposit amount in the deposit's own currency — NOT converted to USD. There
     * is no fiat→USD rate on the frontend, so the conversion is owned by Mixpanel
     * (Lexicon lookup) or by the backend's authoritative events; `amount_usd` is
     * deliberately absent rather than approximated.
     *
     * Sent as a NUMBER, unquoted — a numeric value sent as a string is
     * non-aggregatable in Mixpanel forever (you cannot sum or average it), and it
     * cannot be fixed retroactively.
     */
    amountLocal: number;
    /** ISO currency code of the deposit, e.g. "BRL". */
    currency: string;
    /** Provider-side payment method id. */
    paymentMethod: string;
    /** Human-readable method name, for breakdowns that read well in Mixpanel. */
    paymentMethodName?: string;
    /**
     * AC-814: the backend's canonical method identifier (the transaction `source`,
     * e.g. "APIPRO_MP_DEPOSIT") — the same vocabulary the backend sends as
     * `payment_provider` on deposit_created/success/failed, so a funnel breakdown
     * by `payment_provider` covers this step too. Comes from the payment-methods
     * response (`IBankPayment.provider`); optional until the wallet AC-814 deploy
     * is live in the environment.
     */
    paymentProvider?: string;
}

export const trackDepositStarted = (props: DepositStartedProps): void => {
    trackEvent(MixpanelEvents.DEPOSIT_STARTED, {
        amount_local: props.amountLocal,
        currency: props.currency,
        payment_method: props.paymentMethod,
        payment_method_name: props.paymentMethodName,
        payment_provider: props.paymentProvider,
    });
};

interface DepositInstructionsShownProps {
    /** Which instructions screen was rendered: `p2p` (CLABE tile), `redirect` (voucher / checkout page), `stk_push`, or `processing`. */
    flow: string;
    /** Provider-side payment-method id — same vocabulary as `deposit_started.payment_method`. */
    paymentMethod: string;
    /** Human-readable method name, for breakdowns that read well in Mixpanel. */
    paymentMethodName?: string;
}

export const trackDepositInstructionsShown = (props: DepositInstructionsShownProps): void => {
    trackEvent(MixpanelEvents.DEPOSIT_INSTRUCTIONS_SHOWN, {
        flow: props.flow,
        payment_method: props.paymentMethod,
        payment_method_name: props.paymentMethodName,
    });
};

export const trackDepositTabOpened = (): void => {
    trackEvent(MixpanelEvents.DEPOSIT_TAB_OPENED);
};

export const trackWithdrawTabOpened = (): void => {
    trackEvent(MixpanelEvents.WITHDRAW_TAB_OPENED);
};

/** Which side of the cashier a selection was made on (spec §11). */
type PaymentType = "deposit" | "withdraw";

interface PaymentMethodSelectedProps {
    /**
     * The concrete method: the provider-side payment-method id for a bank
     * payment, the currency code for crypto. Same vocabulary as
     * `deposit_started.payment_method`, so the two aggregate in one funnel.
     */
    paymentMethod: string;
    /** Human-readable method name, for breakdowns that read well in Mixpanel. */
    paymentMethodName?: string;
    paymentType: PaymentType;
}

export const trackPaymentMethodSelected = (props: PaymentMethodSelectedProps): void => {
    trackEvent(MixpanelEvents.PAYMENT_METHOD_SELECTED, {
        payment_method: props.paymentMethod,
        payment_method_name: props.paymentMethodName,
        payment_type: props.paymentType,
    });
};

export const trackCurrencySelected = (currency: string): void => {
    trackEvent(MixpanelEvents.CURRENCY_SELECTED, {currency});
};

interface DepositAmountEnteredProps {
    /** In the deposit's own currency. Sent as a NUMBER — see `DepositStartedProps`. */
    amountLocal: number;
    currency: string;
}

export const trackDepositAmountEntered = (props: DepositAmountEnteredProps): void => {
    trackEvent(MixpanelEvents.DEPOSIT_AMOUNT_ENTERED, {
        amount_local: props.amountLocal,
        currency: props.currency,
    });
};

/**
 * Why a cashier submit was rejected on the client (spec §11).
 *
 * Closed union, mapped from the app's own Yup messages by
 * `toValidationErrorCode` — a free-form code would make the funnel's drop-off
 * reason unusable as a breakdown.
 */
export type ValidationErrorCode =
    | "amount_below_min"
    | "amount_above_max"
    | "amount_above_limit"
    | "insufficient_balance"
    | "empty_required_field"
    | "wagering_not_completed"
    | "verification_required"
    | "invalid_field";

/**
 * Yup message key → `error_code`. The forms carry `IYupErrorMessage` objects
 * (`{key, values}`) rather than strings, so the key is the stable identity of a
 * failure and this is the only place that knows the translation of one into the
 * other.
 *
 * `Errors.maxValueIs` is only reachable from the crypto withdrawal form (the
 * fiat builder raises the limit-specific keys instead), hence the withdraw-side
 * code.
 */
const YUP_KEY_TO_ERROR_CODE: Record<string, ValidationErrorCode> = {
    "Errors.requiredField": "empty_required_field",
    "Errors.minValueIs": "amount_below_min",
    "Errors.maxValueIs": "amount_above_limit",
    "Errors.insufficientBalance": "insufficient_balance",
    "Errors.depositTransactionLimitExceeded": "amount_above_max",
    "Errors.withdrawalLimitExceeded": "amount_above_limit",
    "Errors.maxLengthIs": "invalid_field",
};

/**
 * Resolves a react-hook-form error message into an `error_code`. The message is
 * `unknown` because Yup's typing calls it a `string` while these schemas return
 * an `IYupErrorMessage` — it is validated here rather than cast.
 */
export const toValidationErrorCode = (message: unknown): ValidationErrorCode => {
    if (message && typeof message === "object" && "key" in message) {
        const {key} = message as IYupErrorMessage;
        return YUP_KEY_TO_ERROR_CODE[key] ?? "invalid_field";
    }
    return "invalid_field";
};

interface ValidationFailedProps {
    errorCode: ValidationErrorCode;
    /** The field that failed. `amount`, a credential field name, or `address`. */
    field: string;
}

export const trackDepositValidationFailed = (props: ValidationFailedProps): void => {
    trackEvent(MixpanelEvents.DEPOSIT_VALIDATION_FAILED, {
        error_code: props.errorCode,
        field: props.field,
    });
};

export const trackWithdrawValidationFailed = (props: ValidationFailedProps): void => {
    trackEvent(MixpanelEvents.WITHDRAW_VALIDATION_FAILED, {
        error_code: props.errorCode,
        field: props.field,
    });
};

interface WithdrawStartedProps {
    /**
     * Only crypto withdrawals carry one: their amount is *entered* in USD and the
     * crypto rate comes from `/api/v1/prices`. Fiat withdrawals have no
     * frontend fiat→USD rate, so they send `amount_local` alone rather than an
     * approximation — see `DepositStartedProps`.
     */
    amountUsd?: number;
    /** The amount in `currency`: the crypto amount, or the fiat amount. */
    amountLocal?: number;
    /** Target currency of the payout — the crypto symbol, or the fiat code. */
    currency: string;
    /** `crypto`, or the provider-side payment-method id for a bank payout. */
    paymentMethod: string;
}

export const trackWithdrawStarted = (props: WithdrawStartedProps): void => {
    trackEvent(MixpanelEvents.WITHDRAW_STARTED, {
        amount_usd: props.amountUsd,
        amount_local: props.amountLocal,
        currency: props.currency,
        payment_method: props.paymentMethod,
    });
};

interface CryptoAssetProps {
    /** Crypto currency symbol, e.g. "USDT". */
    currency: string;
    /** Network / chain, e.g. "TRC20". */
    chain: string;
}

export const trackCryptoChainSelected = (props: CryptoAssetProps): void => {
    trackEvent(MixpanelEvents.CRYPTO_CHAIN_SELECTED, {
        chain: props.chain,
        currency: props.currency,
    });
};

export const trackWalletAddressCopied = (props: CryptoAssetProps): void => {
    trackEvent(MixpanelEvents.WALLET_ADDRESS_COPIED, {
        currency: props.currency,
        chain: props.chain,
    });
};

export const trackQrCodeOpened = (props: CryptoAssetProps): void => {
    trackEvent(MixpanelEvents.QR_CODE_OPENED, {
        currency: props.currency,
        chain: props.chain,
    });
};

interface IPaidClickedProps {
    /** The provider transaction the user claims to have paid. */
    depositId: string;
    currency: string;
}

export const trackIPaidClicked = (props: IPaidClickedProps): void => {
    trackEvent(MixpanelEvents.I_PAID_CLICKED, {
        deposit_id: props.depositId,
        currency: props.currency,
    });
};

/**
 * `bonus_status` (spec §13). The spec names six statuses; the app's engine has
 * more (an offer that has not materialised has no status at all), so the union
 * is derived from `WelcomeBonusStatusesEnum` rather than hand-written — a new
 * engine status is then a compile error in `BONUS_STATUSES` below, not a silent
 * gap in the funnel.
 */
export type BonusStatus =
    | Lowercase<`${WelcomeBonusStatusesEnum}`>
    /** OFFERED_* rows: evaluated at request time, no DB row and no status yet. */
    | "offered"
    | "offered_locked";

const BONUS_STATUSES: Record<WelcomeBonusStatusesEnum, BonusStatus> = {
    [WelcomeBonusStatusesEnum.NOT_CLAIM]: "not_claim",
    [WelcomeBonusStatusesEnum.CLAIM]: "claim",
    [WelcomeBonusStatusesEnum.AVAILABLE]: "available",
    [WelcomeBonusStatusesEnum.ACTIVE]: "active",
    [WelcomeBonusStatusesEnum.COMPLETED]: "completed",
    [WelcomeBonusStatusesEnum.BURN]: "burn",
    [WelcomeBonusStatusesEnum.NOT_AVAILABLE]: "not_available",
    [WelcomeBonusStatusesEnum.LOCKED]: "locked",
    [WelcomeBonusStatusesEnum.SPINS_PENDING]: "spins_pending",
    [WelcomeBonusStatusesEnum.AWAITING_DEPOSIT]: "awaiting_deposit",
};

interface BonusEventProps {
    /** The bonus template UUID — always present, on offers too. */
    bonusId: string;
    /**
     * Which deposit step the bonus belongs to (`triggerParams.depositNumber`).
     * `null` for a bonus that is not part of the deposit ladder.
     */
    bonusNumber: number | null;
    bonusStatus: BonusStatus;
}

/** The three bonus properties, read off a bonus row (spec §13). */
export const toBonusEventProps = (bonus: IBonus): BonusEventProps => {
    const depositNumber = Number(bonus.triggerParams?.depositNumber);

    return {
        bonusId: bonus.bonusId,
        bonusNumber: Number.isFinite(depositNumber) ? depositNumber : null,
        bonusStatus: bonus.status
            ? BONUS_STATUSES[bonus.status]
            : bonus.eligibility === BonusEligibilityEnum.OFFERED_LOCKED
                ? "offered_locked"
                : "offered",
    };
};

const toBonusProperties = (props: BonusEventProps): Record<string, unknown> => ({
    bonus_id: props.bonusId,
    bonus_number: props.bonusNumber,
    bonus_status: props.bonusStatus,
});

export const trackWelcomeBonusCardClicked = (props: BonusEventProps): void => {
    trackEvent(MixpanelEvents.WELCOME_BONUS_CARD_CLICKED, toBonusProperties(props));
};

export const trackBonusUseClicked = (props: BonusEventProps): void => {
    trackEvent(MixpanelEvents.BONUS_USE_CLICKED, toBonusProperties(props));
};

export const trackBonusTurnOnClicked = (props: BonusEventProps): void => {
    trackEvent(MixpanelEvents.BONUS_TURN_ON_CLICKED, toBonusProperties(props));
};

interface BonusSwitchClickedProps {
    fromBonusId: string;
    toBonusId: string;
}

export const trackBonusSwitchClicked = (props: BonusSwitchClickedProps): void => {
    trackEvent(MixpanelEvents.BONUS_SWITCH_CLICKED, {
        from_bonus_id: props.fromBonusId,
        to_bonus_id: props.toBonusId,
    });
};

/**
 * Activating a bonus while another one is ACTIVE is a *switch*: only one bonus
 * wagers at a time, so the engine pauses the other. The two spec events are
 * therefore the same click, told apart by what was wagering at the time.
 */
export const trackBonusActivationClicked = (bonus: IBonus, activeBonus: IBonus | undefined): void => {
    if (activeBonus && activeBonus.bonusId !== bonus.bonusId) {
        trackBonusSwitchClicked({fromBonusId: activeBonus.bonusId, toBonusId: bonus.bonusId});
        return;
    }
    trackBonusTurnOnClicked(toBonusEventProps(bonus));
};

interface BonusAllowedGamesOpenedProps {
    bonusId: string;
    bonusNumber: number | null;
}

export const trackBonusAllowedGamesOpened = (props: BonusAllowedGamesOpenedProps): void => {
    trackEvent(MixpanelEvents.BONUS_ALLOWED_GAMES_OPENED, {
        bonus_id: props.bonusId,
        bonus_number: props.bonusNumber,
    });
};

/** Where the player came into a game from (spec §14). */
export type GameOpenSource =
    | "casino"
    | "search"
    | "favorites"
    | "banner"
    | "bonus_allowed_games";

/**
 * The surface of the *last* click that routed to a game.
 *
 * `game_opened` is emitted by the game page rather than by the ~7 grids that
 * link into it, because only the page knows the game actually launched — and it
 * is also the only place that sees a deep link, a PWA shortcut or the free-spins
 * "Play". The price is that the page cannot know which grid the click came from,
 * so the grids leave it here on their way out and the page consumes it.
 *
 * Cleared on read: an unattributed entry (deep link, reload) must report `null`,
 * not inherit the source of a card the player clicked ten minutes ago.
 */
let pendingGameOpenSource: GameOpenSource | null = null;

export const rememberGameOpenSource = (source: GameOpenSource): void => {
    pendingGameOpenSource = source;
};

interface GameProps {
    gameId: string;
    gameName: string;
    provider: string;
}

export const trackGameOpened = (props: GameProps): void => {
    const openSource = pendingGameOpenSource;
    pendingGameOpenSource = null;

    trackEvent(MixpanelEvents.GAME_OPENED, {
        game_id: props.gameId,
        game_name: props.gameName,
        provider: props.provider,
        open_source: openSource,
    });
};

interface PlayClickedProps {
    gameId: string;
    /** Absent where the click site only holds the id (the free-spins bonus row). */
    gameName?: string;
    provider?: string;
}

export const trackPlayClicked = (props: PlayClickedProps): void => {
    trackEvent(MixpanelEvents.PLAY_CLICKED, {
        game_id: props.gameId,
        game_name: props.gameName,
        provider: props.provider,
    });
};

interface GameClosedProps {
    gameId: string;
    provider: string;
    /** Seconds between the game launching and the page being left. */
    durationSec: number;
}

export const trackGameClosed = (props: GameClosedProps): void => {
    trackEvent(MixpanelEvents.GAME_CLOSED, {
        game_id: props.gameId,
        provider: props.provider,
        duration_sec: props.durationSec,
    });
};

/**
 * Profile / verification (spec §15). All five carry no properties: the only data
 * these surfaces hold is the phone number, the email and the OTP — none of which
 * may ever reach Mixpanel.
 */
export const trackProfileOpened = (): void => {
    trackEvent(MixpanelEvents.PROFILE_OPENED);
};

export const trackAddPhoneClicked = (): void => {
    trackEvent(MixpanelEvents.ADD_PHONE_CLICKED);
};

export const trackConfirmPhoneClicked = (): void => {
    trackEvent(MixpanelEvents.CONFIRM_PHONE_CLICKED);
};

export const trackAddEmailClicked = (): void => {
    trackEvent(MixpanelEvents.ADD_EMAIL_CLICKED);
};

export const trackConfirmEmailClicked = (): void => {
    trackEvent(MixpanelEvents.CONFIRM_EMAIL_CLICKED);
};

export const trackPwaInstallPromptShown = (): void => {
    trackEvent(MixpanelEvents.PWA_INSTALL_PROMPT_SHOWN);
};

export const trackPwaInstallClicked = (): void => {
    trackEvent(MixpanelEvents.PWA_INSTALL_CLICKED);
};

export const trackPwaInstalled = (): void => {
    trackEvent(MixpanelEvents.PWA_INSTALLED);
};

/** How the app is being displayed (spec §16). */
export type DisplayMode = "browser" | "standalone" | "fullscreen" | "standalone_ios";

export const trackPwaOpened = (displayMode: DisplayMode): void => {
    trackEvent(MixpanelEvents.PWA_OPENED, {display_mode: displayMode});
};

export const trackPushPermissionRequested = (): void => {
    trackEvent(MixpanelEvents.PUSH_PERMISSION_REQUESTED);
};

/**
 * The outcome of the browser's permission prompt. `default` — the player closed
 * it without choosing — is neither granted nor denied, and is deliberately not
 * reported as either: the difference between a refusal and a non-answer is the
 * whole point of asking.
 */
export const trackPushPermissionResult = (permission: NotificationPermission): void => {
    if (permission === "granted") {
        trackEvent(MixpanelEvents.PUSH_PERMISSION_GRANTED);
        return;
    }
    if (permission === "denied") {
        trackEvent(MixpanelEvents.PUSH_PERMISSION_DENIED);
    }
};
